/* ---------- Hero: the promise beside a ray-traced design ---------- */
.hero {
  /* Same overlap as the home hero: cancel the flow space the sticky header reserves. */
  margin-top: calc(-1 * (var(--header-h) + 2 * var(--header-margin)));
  padding-block: calc(var(--header-h) + 2 * var(--header-margin) + var(--sp-12)) var(--sp-20);
  background: var(--surface-alt);
}
.heroGrid {
  display: grid;
  gap: var(--sp-12);
  align-items: center;
}
.heroText > * {
  max-width: 38rem;
}
.heroTitle {
  /* Room under the breadcrumb, which the dropped eyebrow label used to provide. */
  margin-top: var(--sp-6);
  font-size: clamp(2.4rem, 3.4vw + 1rem, 4.25rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  text-wrap: balance;
}
.heroLead {
  margin-top: var(--sp-5);
  font-size: var(--fs-lg);
  color: var(--text);
}
.heroActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-4) var(--sp-8);
  margin-top: var(--sp-8);
}
.heroLink {
  font-weight: 600;
  color: var(--text-strong);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.3em;
}
.heroLink:hover {
  color: var(--accent-text);
}
.heroMedia {
  position: relative;
}
.heroImage {
  border-radius: var(--radius-lg);
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-lg);
}
/* One fact about the picture, pinned to it like a label. */
.heroChip {
  position: absolute;
  top: var(--sp-4);
  left: var(--sp-4);
  margin: 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--radius);
  background: rgba(8, 13, 19, 0.72);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: #f5f7fa;
  font-size: var(--fs-sm);
  font-weight: 600;
}
@media (min-width: 64rem) {
  .heroGrid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
    gap: var(--sp-16);
  }
}

/* ---------- The signature: the data stacked into one model ----------
   Each layer is a slanted slab, the way a plan's layers look stacked in 3D; the proposed
   design sits on top in the accent colour. The list runs top to bottom as drawn. */
.stackRow {
  display: grid;
  gap: var(--sp-12);
  align-items: center;
}
.layers {
  display: grid;
  gap: var(--sp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}
.layers li {
  display: grid;
  grid-template-columns: 10rem minmax(0, 1fr);
  align-items: center;
  gap: var(--sp-5);
}
.slab {
  height: 3.4rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: linear-gradient(135deg, var(--surface-raised), var(--surface-alt));
  transform: perspective(20rem) rotateX(48deg) skewX(-18deg);
  box-shadow: 0 10px 16px rgba(0, 0, 0, 0.35);
}
.layers li[data-design] .slab {
  border-color: var(--accent);
  background: linear-gradient(
    135deg,
    color-mix(in srgb, var(--accent) 55%, transparent),
    color-mix(in srgb, var(--accent) 20%, transparent)
  );
}
.layerText {
  display: grid;
  gap: 2px;
}
.layerText strong {
  color: var(--text-strong);
}
.layerText span {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
.layers li[data-design] .layerText strong {
  color: var(--accent-text);
}
.result {
  margin: 0;
}
@media (max-width: 47.99rem) {
  .layers li {
    grid-template-columns: 6.5rem minmax(0, 1fr);
  }
  .slab {
    height: 2.6rem;
  }
}
.resultImage {
  border-radius: var(--radius-lg);
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-lg);
}
.result figcaption {
  margin-top: var(--sp-3);
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
@media (min-width: 64rem) {
  .stackRow {
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: var(--sp-16);
  }
}

/* ---------- Picture beside text: alternatives, and delivery ---------- */
.split {
  display: grid;
  gap: var(--sp-10);
  align-items: center;
}
.splitImage {
  border-radius: var(--radius-lg);
  box-shadow: 0 0 0 1px var(--border);
}
.splitText > h2 {
  margin: 0 0 var(--sp-5);
}
.splitText p {
  margin: 0 0 var(--sp-4);
  max-width: 36rem;
  color: var(--text);
}
.splitText > a {
  display: inline-block;
  margin-top: var(--sp-2);
}
/* The turnaround as a figure, before the paragraphs that qualify it. */
.splitText .turnaround {
  display: grid;
  gap: var(--sp-1);
  margin-bottom: var(--sp-6);
  padding-left: var(--sp-4);
  border-left: 3px solid var(--accent);
}
.turnaround strong {
  font-family: var(--font-display);
  font-size: clamp(1.8rem, 1.1rem + 2vw, 2.75rem);
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: var(--text-strong);
}
.turnaround span {
  color: var(--text);
}
@media (min-width: 64rem) {
  .split {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    gap: var(--sp-16);
  }
  .delivery {
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  }
}
